import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../services/api';

interface BrandingState {
  logoUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
  tenantName: string;
  isLoading: boolean;
  isLoaded: boolean;
  fetchBranding: () => Promise<void>;
  updateBranding: (data: Partial<Pick<BrandingState, 'logoUrl' | 'faviconUrl' | 'primaryColor' | 'secondaryColor'>>) => void;
  applyColors: () => void;
  applyFavicon: () => void;
}

// Helper to generate color shades from a base color
function hexToHSL(hex: string): { h: number; s: number; l: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return { h: 0, s: 0, l: 0 };

  let r = parseInt(result[1], 16) / 255;
  let g = parseInt(result[2], 16) / 255;
  let b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
}

function HSLToHex(h: number, s: number, l: number): string {
  s /= 100;
  l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

function generateColorShades(baseColor: string): Record<string, string> {
  const { h, s } = hexToHSL(baseColor);
  return {
    50: HSLToHex(h, Math.max(s - 30, 10), 97),
    100: HSLToHex(h, Math.max(s - 20, 15), 94),
    200: HSLToHex(h, Math.max(s - 10, 20), 86),
    300: HSLToHex(h, s, 76),
    400: HSLToHex(h, s, 64),
    500: HSLToHex(h, s, 50),
    600: HSLToHex(h, s, 42),
    700: HSLToHex(h, s, 34),
    800: HSLToHex(h, s, 28),
    900: HSLToHex(h, s, 22),
  };
}

export const useBrandingStore = create<BrandingState>()(
  persist(
    (set, get) => ({
      logoUrl: '/logo.png',
      faviconUrl: '/favicon.png',
      primaryColor: '#316D94',
      secondaryColor: '#629DBB',
      tenantName: 'Affiliate Platform',
      isLoading: false,
      isLoaded: false,

      fetchBranding: async () => {
        set({ isLoading: true });
        try {
          const response = await api.get('/branding');
          if (response.data.success) {
            const { branding } = response.data.data;
            set({
              logoUrl: branding.logo_url || '/logo.png',
              faviconUrl: branding.favicon_url || '/favicon.png',
              primaryColor: branding.primary_color || '#316D94',
              secondaryColor: branding.secondary_color || '#629DBB',
              tenantName: branding.name || 'Affiliate Platform',
              isLoaded: true,
            });
            get().applyColors();
            get().applyFavicon();
          }
        } catch (error) {
          // Use defaults if branding fetch fails
          console.error('Failed to fetch branding, using defaults:', error);
          set({
            logoUrl: '/logo.png',
            faviconUrl: '/favicon.png',
            primaryColor: '#316D94',
            secondaryColor: '#629DBB',
            tenantName: 'Affiliate Platform',
            isLoaded: true,
          });
          get().applyColors();
          get().applyFavicon();
        } finally {
          set({ isLoading: false });
        }
      },

      updateBranding: (data) => {
        set(data);
        // Apply colors if they changed
        if (data.primaryColor || data.secondaryColor) {
          get().applyColors();
        }
        // Apply favicon if it changed
        if (data.faviconUrl !== undefined) {
          get().applyFavicon();
        }
      },

      applyColors: () => {
        const { primaryColor, secondaryColor } = get();
        const root = document.documentElement;

        // Generate and apply primary color shades
        const primaryShades = generateColorShades(primaryColor);
        Object.entries(primaryShades).forEach(([shade, color]) => {
          root.style.setProperty(`--color-primary-${shade}`, color);
        });

        // Generate and apply secondary color shades
        const secondaryShades = generateColorShades(secondaryColor);
        Object.entries(secondaryShades).forEach(([shade, color]) => {
          root.style.setProperty(`--color-secondary-${shade}`, color);
        });

        // Also set the base colors
        root.style.setProperty('--color-primary', primaryColor);
        root.style.setProperty('--color-secondary', secondaryColor);
      },

      applyFavicon: () => {
        const { faviconUrl } = get();
        let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          link.type = 'image/png';
          document.head.appendChild(link);
        }
        link.href = faviconUrl || '/favicon.png';
      },
    }),
    {
      name: 'branding-storage',
      partialize: (state) => ({
        logoUrl: state.logoUrl,
        faviconUrl: state.faviconUrl,
        primaryColor: state.primaryColor,
        secondaryColor: state.secondaryColor,
        tenantName: state.tenantName,
      }),
    }
  )
);

export default useBrandingStore;
